/**
 * Button component with variant/size props.
 *
 * Replaces Hugo's .btn-primary-lg, .btn-secondary-md, etc. CSS classes.
 * Tailwind v4 can't resolve custom theme tokens in @apply inside @layer,
 * so we compose utility classes directly in JSX — the React way.
 *
 * Usage:
 *   <Button href="/demo">Book a demo</Button>
 *   <Button href="/tour" variant="secondary" size="lg">Take a tour</Button>
 *   <Button href="/docs" variant="outline" size="sm">Read docs</Button>
 */

import { useEffect, useState } from "react";
import { isLumaUrl, withForwardedUtm, getForwardedSearch } from "~/utils/utm";

type Variant = "primary" | "secondary" | "outline" | "white" | "white-outline" | "white-teal" | "teal" | "teal-outline" | "violet" | "pink" | "pink-outline" | "lime";
type Size = "xl" | "lg" | "md" | "sm";

const BASE = "inline-flex items-center font-medium tracking-[1%] transition-colors duration-300 ease-in-out";

// Canonical variant styles (2026 reskin)
const TEAL = "text-white bg-teal-cta hover:bg-teal-cta-hover active:bg-teal-cta-hover/90";
const TEAL_OUTLINE =
  "text-teal-dark border border-teal-dark hover:bg-teal-dark hover:text-white active:bg-teal-dark/90 active:text-white";
const WHITE = "bg-white text-gray-90 hover:bg-gray-10 active:bg-gray-20";
const WHITE_OUTLINE =
  "border border-white text-white hover:bg-white hover:text-charcoal active:bg-gray-10 active:text-charcoal";
// White fill with dark-teal (#00343A) copy
const WHITE_TEAL = "bg-white text-teal-dark hover:bg-gray-10 active:bg-gray-20";
const VIOLET = "text-white bg-violet hover:bg-purple-60 active:bg-purple-60";
const PINK = "text-charcoal bg-pink-accent hover:bg-pink-accent/85 active:bg-pink-accent/75";
const PINK_OUTLINE =
  "text-pink-accent border border-pink-accent hover:bg-pink-accent hover:text-charcoal active:bg-pink-accent/90 active:text-charcoal";
const LIME = "text-charcoal bg-green-60 hover:bg-green-60/85 active:bg-green-60/75";

const VARIANTS: Record<Variant, string> = {
  // Canonical variants
  teal: TEAL,
  "teal-outline": TEAL_OUTLINE,
  white: WHITE,
  "white-outline": WHITE_OUTLINE,
  "white-teal": WHITE_TEAL,
  violet: VIOLET,
  pink: PINK,
  "pink-outline": PINK_OUTLINE,
  lime: LIME,
  // Legacy aliases (map to new colors so nothing breaks)
  primary: TEAL,
  secondary: TEAL_OUTLINE,
  outline: WHITE,
};

// Resolve legacy aliases to their canonical variant for rounding/sizing
function resolveVariant(variant: Variant): "teal" | "teal-outline" | "white" | "white-outline" | "violet" | "pink" | "lime" {
  if (variant === "primary" || variant === "teal") return "teal";
  if (variant === "secondary" || variant === "teal-outline") return "teal-outline";
  if (variant === "white-outline" || variant === "white-teal") return "white-outline";
  if (variant === "violet") return "violet";
  if (variant === "pink") return "pink";
  if (variant === "lime") return "lime";
  // outline, white
  return "white";
}

function getRounded(variant: Variant, size: Size): string {
  const resolved = resolveVariant(variant);
  if (resolved === "white") return "rounded-lg";
  // teal, teal-outline, white-outline, violet, pink, lime: xl/lg → rounded-10, md/sm → rounded-lg
  return size === "xl" || size === "lg" ? "rounded-10" : "rounded-lg";
}

// Sizing by resolved canonical variant
const CANONICAL_SIZES: Record<Size, Record<"teal" | "teal-outline" | "white" | "white-outline" | "violet" | "pink" | "lime", string>> = {
  xl: {
    teal: "px-10 py-2.5 text-19 leading-normal",
    "teal-outline": "px-10 py-2.5 text-19 leading-normal",
    white: "gap-1.5 px-10 py-2.5 text-19 leading-normal",
    "white-outline": "px-10 py-2.5 text-19 leading-normal",
    violet: "px-10 py-2.5 text-19 leading-normal",
    pink: "px-10 py-2.5 text-19 leading-normal",
    lime: "px-10 py-2.5 text-19 leading-normal",
  },
  lg: {
    teal: "px-8 py-2 leading-[1.625rem]",
    "teal-outline": "px-8 py-2 leading-[1.625rem]",
    white: "gap-1.5 px-8 py-2 leading-[1.625rem]",
    "white-outline": "px-8 py-2 leading-[1.625rem]",
    violet: "px-8 py-2 leading-[1.625rem]",
    pink: "px-8 py-2 leading-[1.625rem]",
    lime: "px-8 py-2 leading-[1.625rem]",
  },
  md: {
    teal: "px-5 py-1.5 leading-normal",
    "teal-outline": "px-5 py-1.5 leading-normal",
    white: "gap-1.5 px-5 py-1 leading-[1.75rem]",
    "white-outline": "px-5 py-1.5 leading-normal",
    violet: "px-5 py-1.5 leading-normal",
    pink: "px-5 py-1.5 leading-normal",
    lime: "px-5 py-1.5 leading-normal",
  },
  sm: {
    teal: "px-4 py-1 text-13 leading-[1.5rem]",
    "teal-outline": "gap-1.5 px-4 py-1.5 text-13 leading-tight",
    white: "gap-1.5 px-4 py-1 text-13 leading-[1.5rem]",
    "white-outline": "px-4 py-1 text-13 leading-[1.5rem]",
    violet: "px-4 py-1 text-13 leading-[1.5rem]",
    pink: "px-4 py-1 text-13 leading-[1.5rem]",
    lime: "px-4 py-1 text-13 leading-[1.5rem]",
  },
};

// Full SIZES record including legacy aliases (resolved to canonical)
const SIZES: Record<Size, Record<Variant, string>> = Object.fromEntries(
  Object.entries(CANONICAL_SIZES).map(([size, variants]) => [
    size,
    {
      ...variants,
      primary: variants.teal,
      secondary: variants["teal-outline"],
      outline: variants.white,
      "white-outline": variants.white,
      "white-teal": variants["white-outline"],
      "pink-outline": variants.pink,
    },
  ]),
) as Record<Size, Record<Variant, string>>;

interface ButtonProps {
  href?: string;
  variant?: Variant;
  size?: Size;
  className?: string;
  children: React.ReactNode;
  target?: string;
  rel?: string;
  onClick?: () => void;
  type?: "button" | "submit";
}

export function Button({
  href,
  variant = "primary",
  size = "lg",
  className = "",
  children,
  target,
  rel,
  onClick,
  type = "button",
}: ButtonProps) {
  const classes = `${BASE} ${VARIANTS[variant]} ${SIZES[size][variant]} ${getRounded(variant, size)} ${className}`.trim();

  if (href) {
    if (isLumaUrl(href)) {
      return (
        <UtmForwardingLink href={href} className={classes} target={target} rel={rel}>
          {children}
        </UtmForwardingLink>
      );
    }
    return (
      <a href={href} className={classes} target={target} rel={rel}>
        {children}
      </a>
    );
  }

  return (
    <button type={type} className={classes} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * Anchor that forwards the session's tracking params (`utm_*` + `gclid`) onto a
 * lu.ma destination.
 *
 * SSR renders the original href (so there is no hydration mismatch); once
 * mounted on the client, the effect rewrites the href to include the forwarded
 * UTM params. Rewriting the real DOM href — rather than intercepting onClick
 * — covers every navigation modality: left-click, middle-click, cmd/ctrl-click
 * (open in new tab), and "Copy link address".
 */
function UtmForwardingLink({
  href,
  className,
  target,
  rel,
  children,
}: {
  href: string;
  className: string;
  target?: string;
  rel?: string;
  children: React.ReactNode;
}) {
  const [resolvedHref, setResolvedHref] = useState(href);

  useEffect(() => {
    // getForwardedSearch() merges the session-persisted UTMs with the live URL,
    // so the lu.ma link stays attributed even when the visitor reaches this CTA
    // after navigating away from the original influencer landing URL.
    setResolvedHref(withForwardedUtm(href, getForwardedSearch()));
  }, [href]);

  return (
    <a href={resolvedHref} className={className} target={target} rel={rel}>
      {children}
    </a>
  );
}
